/**
=========================================================
* Soft UI Dashboard React - v4.0.1
=========================================================

* Product Page: https://www.creative-tim.com/product/soft-ui-dashboard-react
* Copyright 2023 Creative Tim (https://www.creative-tim.com)

Coded by www.creative-tim.com

 =========================================================

* The above copyright notice and this permission notice shall be included in all copies or substantial portions of the Software.
*/

import PropTypes from "prop-types";
import { useTheme } from "@mui/material/styles";

// @mui material components
import Card from "@mui/material/Card";
import Grid from "@mui/material/Grid";
import Icon from "@mui/material/Icon";

// Soft UI Dashboard React components
import SoftBox from "components/SoftBox";
import SoftTypography from "components/SoftTypography";

function MiniStatisticsCard({ bgColor= "white", title, count, percentage, icon, direction }) {
  const theme = useTheme();
  const isWhiteBg = bgColor === "white";
  // Determine text color based on background
  const textColor = isWhiteBg ? theme.palette.dark.main : theme.palette.white.main;
  // Determine percentage text color
  const percentColor = theme.palette[percentage.color]?.main || theme.palette.success.main;

  return (
    <Card>
      <SoftBox bgColor={bgColor} variant="gradient" color={isWhiteBg ? "dark" : "white"}>
        <SoftBox p={2}>
          <Grid container alignItems="center">
            {direction === "left" && (
              <Grid item>
                <SoftBox
                  variant="gradient"
                  bgColor={isWhiteBg ? icon.color : "white"}
                  color={isWhiteBg ? "white" : "dark"}
                  width="3rem"
                  height="3rem"
                  borderRadius="md"
                  display="flex"
                  justifyContent="center"
                  alignItems="center"
                  shadow="md"
                  mr={1}
                >
                  <Icon fontSize="small" color="inherit">
                    {icon.component}
                  </Icon>
                </SoftBox>
              </Grid>
            )}
            <Grid item xs>
              <SoftBox ml={direction === "left" ? 2 : 0} lineHeight={1}>
                {/* Title */}
                <SoftTypography
                  variant="button"
                  style={{ color: textColor }}
                  textTransform="capitalize"
                  fontWeight={title.fontWeight}
                >
                  {title.text}
                </SoftTypography>

                {/* Count and percentage */}
                <SoftTypography
                  variant="h5"
                  fontWeight="bold"
                  style={{ color: textColor }}
                >
                  {count}{" "}
                  <SoftTypography
                    component="span"
                    variant="button"
                    fontWeight="bold"
                    style={{ color: percentColor }}
                  >
                    {percentage.text}
                  </SoftTypography>
                </SoftTypography>
              </SoftBox>
            </Grid>
            {direction === "right" && (
              <Grid item>
                <SoftBox
                  variant="gradient"
                  bgColor={isWhiteBg ? icon.color : "white"}
                  color={isWhiteBg ? "white" : "dark"}
                  width="3rem"
                  height="3rem"
                  ml="auto"
                  borderRadius="md"
                  display="flex"
                  justifyContent="center"
                  alignItems="center"
                  shadow="md"
                >
                  <Icon fontSize="small" color="inherit">
                    {icon.component}
                  </Icon>
                </SoftBox>
              </Grid>
            )}
          </Grid>
        </SoftBox>
      </SoftBox>
    </Card>
  );
}

// Default props
MiniStatisticsCard.defaultProps = {
  bgColor: "white",
  title: { fontWeight: "medium", text: "" },
  percentage: { color: "success", text: "" },
  direction: "right",
};

// PropTypes
MiniStatisticsCard.propTypes = {
  bgColor: PropTypes.oneOf([
    "white", "primary", "secondary", "info", "success", "warning", "error", "dark",
  ]),
  title: PropTypes.shape({
    fontWeight: PropTypes.oneOf(["light", "regular", "medium", "bold"]),
    text: PropTypes.string,
  }),
  count: PropTypes.oneOfType([PropTypes.string, PropTypes.number]).isRequired,
  percentage: PropTypes.shape({
    color: PropTypes.oneOf([
      "primary", "secondary", "info", "success", "warning", "error", "dark", "white",
    ]),
    text: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
  }),
  icon: PropTypes.shape({
    color: PropTypes.oneOf([
      "primary", "secondary", "info", "success", "warning", "error", "dark",
    ]).isRequired,
    component: PropTypes.node.isRequired,
  }).isRequired,
  direction: PropTypes.oneOf(["right", "left"]),
};

export default MiniStatisticsCard;
